import Roboto from '@lmt/unicorn-shared/fonts/roboto-regular.ttf';
import RobotoBold from '@lmt/unicorn-shared/fonts/roboto-bold.ttf';

import type {
  pdf,
  Document,
  Page,
  Image,
  View,
  Text,
  Link,
  Note,
  Canvas,
  Line,
  Polyline,
  Polygon,
  StyleSheet,
  Font,
  Svg as SvgInner,
  Path,
  Rect,
  Circle,
  Ellipse,
  Tspan,
  G,
  Stop,
  Defs,
  ClipPath,
  LinearGradient,
  RadialGradient,
  PDFDownloadLink,
  PDFRenderer,
} from '@react-pdf/renderer';
import React from 'react';

type Svg = React.Component<
  React.PropsWithChildren<React.ComponentProps<typeof SvgInner>>
>;
export type {
  pdf,
  Document,
  Page,
  Image,
  View,
  Text,
  Link,
  Note,
  Canvas,
  Svg,
  Line,
  Polyline,
  Polygon,
  StyleSheet,
  Font,
  Path,
  Rect,
  Circle,
  Ellipse,
  Tspan,
  G,
  Stop,
  Defs,
  ClipPath,
  LinearGradient,
  RadialGradient,
  PDFDownloadLink,
  PDFRenderer,
};

export let RPDF: {
  pdf: typeof pdf;
  PDFRenderer: typeof PDFRenderer;
  Document: typeof Document;
  Page: typeof Page;
  StyleSheet: typeof StyleSheet;
  Font: typeof Font;
  Image: typeof Image;
  View: typeof View;
  Text: typeof Text;
  Link: typeof Link;
  Note: typeof Note;
  Canvas: typeof Canvas;
  Svg: any;
  Line: typeof Line;
  Polyline: typeof Polyline;
  Polygon: typeof Polygon;
  Path: typeof Path;
  Rect: typeof Rect;
  Circle: typeof Circle;
  Ellipse: typeof Ellipse;
  Tspan: typeof Tspan;
  G: typeof G;
  Stop: typeof Stop;
  Defs: typeof Defs;
  ClipPath: typeof ClipPath;
  LinearGradient: typeof LinearGradient;
  RadialGradient: typeof RadialGradient;
  PDFDownloadLink: typeof PDFDownloadLink;
  calendarStyles: ReturnType<typeof createStyles>['calendarStyles'];
  styles: ReturnType<typeof createStyles>['styles'];
  chartStyles: ReturnType<typeof createStyles>['chartStyles'];
  tableColumnsStyles: ReturnType<typeof createStyles>['tableColumnsStyles'];
} = {} as any;

let _promise: Promise<any> | null;
let _isLoaded: boolean;
let _error: any;

export function useReactPdf() {
  if (!_isLoaded) {
    if (!_promise) {
      _promise = initReactPdf();
    }
    // will trigger Suspense
    throw _promise;
  }
  if (_error) {
    throw _error;
  }
}

export function initReactPdf() {
  return Promise.all([
    import('@react-pdf/renderer').then((x) => {
      RPDF = x as any;
      RPDF = { ...x, ...createStyles() };

      RPDF.Font.register({
        family: 'Roboto',
        src: Roboto as any,
      });

      RPDF.Font.register({
        family: 'Roboto',
        fontWeight: 'bold',
        src: RobotoBold as any,
      });
    }),
  ])
    .finally(() => (_isLoaded = true))
    .catch((e) => (_error = e));
}

function createStyles() {
  return {
    styles: RPDF.StyleSheet.create({
      page: {
        flexDirection: 'column',
        backgroundColor: 'white',
        padding: '39px 35px 0 35px',
      },
      headerContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '20px',
      },
      headerText: {
        color: '#1E265D',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '18px',
      },
      infoContainer: {
        marginTop: '20px',
        flexDirection: 'row',
        marginBottom: '20px',
      },
      titleContainer: {
        height: '24px',
        padding: '0 16px',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#f3f6fa',
      },
      titleText: {
        color: '#1E265D',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '12px',
        marginRight: '4px',
      },
      visitsCountContainer: {
        marginTop: '2px',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        padding: '5px 8px',
        backgroundColor: '#D3DCEA',
      },
      visitsCountText: {
        marginLeft: '4px',
        marginBottom: '1px',
        color: '#4B527E',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '9px',
      },
      rowContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: '4px',
        padding: '12px 8px',
        height: '38px',
      },
      darkRowBackground: {
        backgroundColor: '#f3f6fa',
      },
      lightRowBackground: {
        backgroundColor: 'white',
      },
      rowText: {
        color: '#1E265D',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '9px',
      },
      rowImage: {
        marginRight: '3px',
      },
      columnsContainer: {
        marginTop: '20px',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '0 8px',
        height: '14px',
      },
      columnText: {
        color: '#828282',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '9px',
      },
      paginationContainer: {
        paddingTop: '30px',
        paddingBottom: '17px',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        borderTopStyle: 'dashed',
        borderTopWidth: '1px',
        borderTopColor: '#EBEBEB',
        marginTop: 'auto',
      },
      paginationDataContainer: {
        width: '50%',
        flexDirection: 'row',
      },
      paginationText: {
        color: '#404040',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '9px',
      },
      paginationDate: {
        marginLeft: 'auto',
      },
      paginationPages: {
        marginRight: 'auto',
        width: '100px',
        marginLeft: '-50px',
        flexDirection: 'row',
        justifyContent: 'center',
      },
      emptyContainer: {
        margin: '24px 0',
        borderColor: '#F3F6FA',
        borderWidth: '3px',
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
      },
      emptyTextContainer: {
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '38px',
      },
      emptyTitle: {
        color: '#1E265D',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '12px',
        padding: '2px',
      },
      emptyText: {
        color: '#828282',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '9px',
        padding: '2px',
      },
    }),

    tableColumnsStyles: RPDF.StyleSheet.create({
      timeColumn: {
        width: '114px',
      },
      patientColumn: {
        width: '132px',
      },
      doctorColumn: {
        width: '98px',
      },
      visitTypeColumn: {
        width: '164px',
        flexDirection: 'row',
        alignItems: 'center',
      },
    }),

    chartStyles: RPDF.StyleSheet.create({
      container: {
        width: '100%',
        marginLeft: '24px',
        flexDirection: 'column',
      },
      dataContainer: {
        marginTop: '21px',
        flexDirection: 'row',
      },
      listContainer: {
        flexDirection: 'column',
        marginLeft: '30px',
      },
      listRow: {
        flexDirection: 'row',
        alignItems: 'center',
        height: '16px',
        marginBottom: '4px',
      },
      listTypeText: {
        marginLeft: '5px',
        color: '#1E265D',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '10px',
      },
      listCountText: {
        marginLeft: '5px',
        color: '#1E265D',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '9px',
      },
      chartContainer: {
        width: '140',
      },
      charCountText: {
        color: '#1E265D',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '18px',
      },
      charTypeText: {
        color: '#1E265D',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '12px',
      },
    }),

    calendarStyles: RPDF.StyleSheet.create({
      container: {
        flexDirection: 'column',
        minWidth: '155px',
      },
      calendarContainer: {
        marginLeft: '4px',
        marginTop: '3px',
        color: '#828282',
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '9px',
        flexDirection: 'column',
      },
      monthContainer: {
        width: '147px',
        flexDirection: 'column',
      },
      selectedContainer: {
        fontWeight: 'bold',
        color: '#1E265D',
        borderWidth: '1px',
        borderColor: '#1E265D',
      },
      weekContainer: {
        flexDirection: 'row',
      },
      dayContainer: {
        margin: '1px 1.5px',
        height: '18px',
        width: '18px',
        justifyContent: 'center',
        alignItems: 'center',
      },
      weekDayTitleText: {
        fontWeight: 'bold',
      },
      firstWeek: {
        justifyContent: 'flex-end',
      },
    }),
  };
}
